<template>
  <div class="pa-dev-locked">
    <div class="pa-dev-head">
      <div class="pa-dev-eye">
        {{ t('property.access.devAccountManagerLocked.developerEyebrow', { developer: developerName }) }}
      </div>
      <div class="pa-dev-title">
        {{ t('property.access.devAccountManagerLocked.accountManagersTitle', { count: managers.length }) }}
      </div>
    </div>

    <div class="pa-dev-rows">
      <div
        v-for="(m, i) in managers"
        :key="i"
        class="pa-dev-row"
      >
        <div class="pa-dev-av" />
        <div class="pa-dev-mid">
          <div class="pa-dev-name">
            <span class="pa-blur">{{ m.displayName }}</span>
            <span class="pa-dev-role">{{ m.role }}</span>
          </div>
          <div class="pa-dev-meta">
            <span
              v-if="m.maskedPhone"
              class="pa-blur"
              dir="ltr"
              >{{ m.maskedPhone }}</span
            >
            <span
              v-if="m.maskedEmail"
              class="pa-blur"
              >{{ m.maskedEmail }}</span
            >
          </div>
        </div>
        <div class="pa-dev-actions">
          <span
            class="pa-dev-pill"
            :aria-label="t('property.access.devAccountManagerLocked.lockedAria')"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="4"
                y="11"
                width="16"
                height="10"
                rx="2"
              />
              <path d="M8 11V8a4 4 0 1 1 8 0v3" />
            </svg>
          </span>
        </div>
      </div>
    </div>

    <div class="pa-dev-foot">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 3l8 3v6c0 5-3.4 8.5-8 9.5C7.4 20.5 4 17 4 12V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      <span>{{ t('property.access.devAccountManagerLocked.phoneEmailNote') }}</span>
      <button
        type="button"
        class="pa-dev-link"
        @click="emit('verify-click')"
      >
        <span
          >{{ t('property.access.devAccountManagerLocked.verify') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

defineProps<{
  developerName: string;
  managers: { displayName: string; role: string; maskedPhone?: string; maskedEmail?: string }[];
}>();

const emit = defineEmits<{
  (e: 'verify-click'): void;
}>();
</script>

<style scoped lang="scss">
.pa-dev-locked {
  background: #f8f7f3;
  border-radius: 16px;
  padding: 18px;

  .pa-dev-head {
    margin-bottom: 14px;
  }

  .pa-dev-eye {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(13, 15, 21, 0.62);
    margin-bottom: 6px;
  }

  .pa-dev-title {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 16px;
    letter-spacing: -0.01em;
    color: #0d0f15;
  }

  .pa-dev-rows > * + * {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(13, 15, 21, 0.08);
  }

  .pa-dev-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .pa-dev-av {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(13, 15, 21, 0.1), rgba(13, 15, 21, 0.05));
    flex-shrink: 0;
    filter: blur(2px);
  }

  .pa-dev-mid {
    flex-grow: 1;
    min-width: 0;
  }

  .pa-dev-name {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
    font-weight: 700;
    color: #0d0f15;
    margin-bottom: 4px;
  }

  .pa-dev-role {
    font-size: 11px;
    font-weight: 500;
    color: rgba(13, 15, 21, 0.62);
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .pa-dev-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10.5px;
    color: rgba(13, 15, 21, 0.62);
  }

  .pa-blur {
    filter: blur(4px);
    user-select: none;
  }

  .pa-dev-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
    flex-shrink: 0;
  }

  .pa-dev-pill {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: white;
    border: 1px solid rgba(13, 15, 21, 0.08);
    color: rgba(13, 15, 21, 0.62);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .pa-dev-foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(13, 15, 21, 0.08);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(13, 15, 21, 0.7);
    line-height: 1.45;

    svg {
      color: #126b2f;
      flex-shrink: 0;
    }

    span {
      flex-grow: 1;
    }
  }

  .pa-dev-link {
    color: #0d0f15;
    font-weight: 700;
    margin-inline-start: auto;
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
